<template>
  <div
    :id="eventId"
    class="event-time"
    :class="{'event-time--open': showBooking}"
  >
    <div class="event-time__header">
      <div class="event-time__row">
        <h5 class="event-time__title">
          {{ titleComplete }}
        </h5>
        <small v-if="teaser"><strong>{{ teaser }}</strong></small>
      </div>

      <div
        v-if="parseInt(event.tickets_available) <= parseInt(event.tickets_sold)"
        class="gap__cell gap__cell--booking"
      >
        <button
          type="button"
          disabled="disabled"
          class="action"
        >
          <span class="action__inner"><span>{{ $t('soldOut') }}</span></span>
        </button>
      </div>

      <action
        v-if="parseInt(event.tickets_available) > parseInt(event.tickets_sold)"
        color="tertiary"
        class="event-time__booking"
        :class="{'is-active': showBooking}"
        @click="toggleBooking"
      >
        <icon
          class="event-time__booking-icon"
          type="expand"
        />
        <span
          v-if="!discount"
          class="event-time__booking-text"
        >{{ $t('book') }}</span>
        <span
          v-if="discount"
          class="event-time__booking-text"
        >{{ $t('book') }} + %</span>
      </action>
    </div>

    <transition-height
      v-show="showBooking"
      v-if="parseInt(event.tickets_available) > parseInt(event.tickets_sold)"
      @after-enter="afterBookingOpened"
    >
      <div class="event-time__body">
        <div
          class="event-time__description"
          v-html="event['gap_info_' + $i18n.locale]"
        >
          {{ event['gap_info_' + $i18n.locale] }}
        </div>

        <div class="event-time__tickets">
          <div
            v-for="priceGroupId in event.price_groups"
            :key="priceGroupId"
            class="form-input form-input--inline-label form-input--white"
          >
            <label
              :for="fieldId + '_' + priceGroupId"
              class="form-input__label"
            >
              {{ priceGroupLabel(priceGroupId) }} {{ $t('forPrice') }} {{ getPrice(priceGroupId) }} €
            </label>

            <select
              :id="fieldId + '_' + priceGroupId"
              v-model="selection[fieldId + '_' + priceGroupId]"
              class="form-input__input form-input__input--select"
            >
              <option value="0">
                –
              </option>
              <option
                v-for="n in ticketAmount"
                :key="n"
                :value="n"
                :selected="parseInt(selection[fieldId + '_' + priceGroupId]) === n"
              >
                {{ n }}
              </option>
            </select>
          </div>

          <action
            color="primary"
            :disabled="!anythingSelected"
            @click="checkCart"
          >
            <icon type="cart" />
            {{ $t('addToCart') }}
          </action>
        </div>
      </div>
    </transition-height>

    <modal
      v-if="message.show"
      :title="$t('message.title')"
      size="medium"
      no-close
    >
      <p>{{ message.message }}</p>

      <template #footer>
        <div class="button-group is-aligned-center">
          <action
            v-if="message.showBack"
            color="primary"
            @click="goToBooking"
          >
            {{ $t('message.btnOk') }}
          </action>

          <action
            v-if="message.showDouble"
            color="primary"
            @click="replaceTickets"
          >
            {{ $t('message.btnReplace') }}
          </action>

          <action
            v-if="message.showDouble || message.showBack"
            color="secondary"
            @click="discardEvents"
          >
            {{ $t('message.btnDiscard') }}
          </action>

          <action
            v-if="message.showConflict"
            color="primary"
            @click="keepTickets"
          >
            {{ $t('message.btnKeep') }}
          </action>

          <action
            v-if="message.showConflict"
            color="secondary"
            @click="deleteTickets"
          >
            {{ $t('message.btnDelete') }}
          </action>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import action from './action.vue'
import icon from './icon.vue'
import modal from './modal.vue'
import transitionHeight from './transitionHeight.vue'

import dateHelper from '../functions/dateHelper.js'
import eventBus from '../eventBus.js'
import priceHelper from '../functions/priceHelper.js'
import scrollToArrivalTime from '../functions/scrollToArrivalTime.js'

export default {
  name: 'CEventTime',

  components: {
    action,
    icon,
    modal,
    transitionHeight
  },

  props: {
    event: {
      type: Object,
      required: true
    },
    maxTickets: {
      type: Number,
      default: 14
    },
    reservation: Boolean
  },

  data () {
    return {
      selection: {},
      showBooking: false,
      titleComplete: '',
      title: '',
      time: '',
      timeSubtitle: '',
      discount: '',
      message: {
        show: false,
        showDouble: false,
        showConflict: true,
        showBack: false,
        message: ''
      }
    }
  },

  computed: {
    eventId () {
      return `special-event-${this._uid}`
    },
    fieldId () {
      return dateHelper.formatDate(dateHelper.dateFromString(this.event.date))
    },
    anythingSelected () {
      return Object.keys(this.selection).length
    },
    teaser () {
      return this.event['teaser_' + this.$i18n.locale]
    },
    ticketAmount () {
      let ticketCount = parseInt(this.event.tickets_available) - parseInt(this.event.tickets_sold)
      if (ticketCount === 1) {
        ticketCount = 2
      }
      if (ticketCount > this.maxTickets) {
        ticketCount = this.maxTickets
      }
      return ticketCount
    }
  },

  created () {
    this.titleComplete = this.event['name_' + this.$i18n.locale]
    const timeComps = this.event.from.split(':')
    this.time = timeComps[0] + ':' + timeComps[1]
  },

  methods: {
    toggleBooking () {
      this.showBooking = !this.showBooking
    },
    afterBookingOpened () {
      if (this.showBooking === true) {
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    getPrice (priceGroupId) {
      const priceGroup = this.$store.state.priceGroups[priceGroupId]
      const price = priceHelper.getArticlePrice(priceGroup.preis, priceGroup.price_new, priceGroup.price_new_date, this.event.date)
      return this.formatPrice(price)
    },
    formatPrice (price) {
      return priceHelper.format(price / 100, this.$i18n.locale)
    },
    priceGroupLabel (priceGroupId) {
      const priceGroup = this.$store.state.priceGroups[priceGroupId]
      let label = priceGroup['unterbezeichnung_' + this.$i18n.locale]
      if (label === null) {
        label = priceGroup['text_' + this.$i18n.locale]
      }
      return label
    },
    async checkCart () {
      if (
        this.$store.state.cart.tickets.total_articles > 0 &&
        this.$store.state.cart.events.total_articles === 0 &&
        this.$store.state.cart.tickets.date === this.event.date.split(' ')[0]
      ) {
        this.message.message = this.$t('message.conflictHint', [this.titleComplete])
        this.message.showDouble = false
        this.message.showConflict = true
        this.message.showBack = false
        this.message.show = true

        return
      }

      if (this.$store.state.cart.events.total_articles > 0) {
        this.message.message = this.$t('message.noDoubleEventHint')
        this.message.showDouble = true
        this.message.showConflict = false
        this.message.showBack = false
        this.message.show = true

        return
      }

      this.addToCart()
    },
    addToCart () {
      const payload = {
        date: dateHelper.formatDate(dateHelper.dateFromString(this.event.date)),
        gap: this.event.gap,
        articles: {}
      }

      for (const selectionKey in this.selection) {
        const count = parseInt(this.selection[selectionKey])
        const articleId = parseInt(selectionKey.split('_')[1])
        payload.articles[articleId] = { count: count }
      }

      this.$store.dispatch('addEventTicketsToCart', payload)
      this.$store.commit('setEventTimes', { from: this.event.from, until: this.event.until })
      this.$store.commit('showContainerSidebar', true)

      if (this.reservation) {
        this.message.message = this.$t('message.backToBookingHint')
        this.message.showDouble = false
        this.message.showConflict = false
        this.message.showBack = true
        this.message.show = true
      } else {
        eventBus.emit('closeEvent')
      }

      scrollToArrivalTime(this)
    },
    goToBooking () {
      this.$router.push({ name: 'booking-' + this.$i18n.locale })
      this.$store.commit('setShowTickets', true)
      eventBus.emit('closeEvent')
    },
    replaceTickets () {
      this.addToCart()
    },
    discardEvents () {
      this.message.show = false
    },
    keepTickets () {
      this.message.show = false
      this.addToCart()
    },
    deleteTickets () {
      this.message.show = false
      this.$store.dispatch('removeFromCart', { endpoint: 'tickets', dontResetSelection: true }).then(() => {
        this.addToCart()
      }, error => { eventBus.emit('handleError', error.response.data.error) })
    }
  }
}
</script>

<i18n locale="en">
{
  "book": "Buy now",
  "soldOut": "Sold out",
  "discountInfo": "Discount for this time-slot for online booking only.",
  "discountInfo2": "There are no further discounts for special events",
  "priceGroupInfo": "There are only {0} price groups available:",
  "addToCart": "Add to cart",
  "from": "From",
  "forPrice": "for",
  "message": {
    "title": "Please note",
    "conflictHint": "You have selected the event „{0}“. Do you also want to keep your regular tickets?",
    "noDoubleEventHint": "You can only book a special event of one type within one order process. If you would like to book another special event, you have to start a second, separate order process. Would you like to replace this special event?",
    "backToBookingHint": "You are now leaving the reservation process. Tickets for special events can only be purchased. You are now being redirected to the purchasing process.",
    "btnOk": "OK",
    "btnKeep": "Keep",
    "btnDelete": "Delete",
    "btnReplace": "Replace",
    "btnDiscard": "Discard"
  }
}
</i18n>

<i18n locale="de">
{
  "book": "Buchung",
  "soldOut": "Ausgebucht",
  "discountInfo": "Der Ticketpreis für diese Ankunftszeit ist bei Onlinebuchung reduziert.",
  "discountInfo2": "Bei unseren Sonderveranstaltungen gibt es keine weiteren Rabatte.",
  "priceGroupInfo": "Beachten Sie bitte, dass es nur {0} Preiskategorien gibt:",
  "addToCart": "In den Warenkorb",
  "from": "Ab",
  "forPrice": "für",
  "message": {
    "title": "Hinweis",
    "conflictHint": "Sie haben sich für die Sonderveranstaltung „{0}“ entschieden. Möchten Sie die regulären Eintrittskarten im Warenkorb beibehalten oder löschen?",
    "noDoubleEventHint": "Sie können pro Bestellvorgang nur eine Sonderveranstaltung eines bestimmten Typs buchen. Wenn Sie zusätzlich eine andere Sonderveranstaltung buchen möchten, müssen Sie einen zweiten, separaten Bestellvorgang vornehmen. Möchten Sie diese Sonderveranstaltung ersetzen?",
    "backToBookingHint": "Sie verlassen jetzt die Reservierung. Tickets für Sonderveranstaltungen können nur gekauft werden. Sie werden jetzt in den Kaufprozess weitergeleitet.",
    "btnOk": "OK",
    "btnKeep": "Behalten",
    "btnDelete": "Löschen",
    "btnReplace": "Ersetzen",
    "btnDiscard": "Verwerfen"
  }
}
</i18n>

<style lang="scss">
@import "../css/variables";

.event-time {
  position: relative;
  border-bottom: $border-gap;
}

.event-time--open {
  margin: 0 (-$gutter-sm);

  &::before {
    content: "";

    position: absolute;
    top: -0.125rem;
    right: 0;
    left: 0;
    height: 0.125rem;

    background-color: #fff;
  }

  @media (min-width: $breakpoint-md) {
    margin-left: (-$gutter-md);
    margin-right: (-$gutter-md);
  }
}

.event-time__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375em 0;

  .event-time--open & {
    padding-left: $gutter-sm;
    padding-right: $gutter-sm;

    color: #fff;

    background-color: $color-secondary;
  }

  @media (prefers-reduced-motion: no-preference) {
    transition:
      color 0.2s,
      background-color 0.2s;
  }

  @media (min-width: $breakpoint-md) {
    flex-wrap: nowrap;

    .event-time--open & {
      padding-left: $gutter-md;
      padding-right: $gutter-md;
    }
  }
}

.event-time__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-grow: 1;
}

.event-time__title {
  flex-grow: 1;
  width: 100%;
  margin: 0.25em 0;

  font-weight: 500;

  @media (min-width: $breakpoint-md) {
    width: 100%;
    margin-right: $gutter-md;
  }
}

.event-time__info {
  flex-shrink: 0;
  margin: 0.25em $gutter-sm 0.25em 0;

  @media (min-width: $breakpoint-md) {
    margin-right: $gutter-md;
  }
}

.event-time__discount {
  color: $color-primary;

  .event-time--open & {
    color: inherit;
  }
}

.event-time__booking {
  flex-shrink: 0;
}

.event-time__booking-icon {
  @media (min-width: $breakpoint-sm) {
    display: none;
  }
}

.event-time__booking-text {
  display: none;

  @media (min-width: $breakpoint-sm) {
    display: inline;
  }
}

.event-time__body {
  position: relative;
  background-color: $color-neutral;

  .event-time--open & {
    padding-left: $gutter-sm;
    padding-right: $gutter-sm;
  }

  @media (min-width: $breakpoint-md) {
    display: flex;
    justify-content: space-between;

    .event-time--open & {
      padding-left: $gutter-md;
      padding-right: $gutter-md;
    }
  }
}

.event-time__description {
  padding-top: $gutter-sm;
  font-size: 0.875em;

  > :last-child {
    margin-bottom: 0;
  }

  @media (min-width: $breakpoint-md) {
    max-width: 50%;
    margin-right: $gutter-lg;
    padding-bottom: $gutter-sm;
  }
}

.event-time__tickets {
  display: grid;
  justify-items: start;
  align-content: start;
  gap: $gutter-xs;

  padding-bottom: $gutter-sm;

  @media (min-width: $breakpoint-sm) {
    justify-items: end;
  }

  @media (min-width: $breakpoint-md) {
    padding-top: $gutter-sm;
    text-align: right;
  }
}
</style>
